import type { ProcessedFormComponent } from './types'

type CustomRecord = Record<string, unknown> | null | undefined

const getCustomObject = (component: ProcessedFormComponent): CustomRecord => {
	const custom = (component as unknown as { custom?: unknown }).custom
	return custom && typeof custom === 'object' ? (custom as Record<string, unknown>) : null
}

export const getInitialString = (
	component: ProcessedFormComponent,
	fallback: string,
	customKey: string = 'defaultValue',
	options?: { treatEmptyStringAsUnset?: boolean },
): string => {
	const treatEmptyStringAsUnset = options?.treatEmptyStringAsUnset ?? true
	let candidate: unknown
	try {
		candidate = JSON.parse(component.value as string)
	} catch {
		candidate = component.value
	}
	if (candidate === null || candidate === undefined || (treatEmptyStringAsUnset && candidate === '')) {
		const custom = getCustomObject(component)
		if (
			custom &&
			customKey in custom &&
			custom[customKey] !== null &&
			custom[customKey] !== undefined &&
			custom[customKey] !== ''
		) {
			return String(custom[customKey])
		}
		return fallback
	}
	return String(candidate)
}

export const getInitialBoolean = (
	component: ProcessedFormComponent,
	fallback: boolean,
	customKey: string = 'defaultChecked',
): boolean => {
	const candidate = component.value ? JSON.parse(component.value as string) : undefined

	if (typeof candidate === 'boolean') {
		return candidate
	}
	const custom = getCustomObject(component)
	if (custom && customKey in custom && typeof custom[customKey] === 'boolean') {
		return Boolean(custom[customKey])
	}
	return fallback
}

export const getInitialNumber = (
	component: ProcessedFormComponent,
	fallback: number,
	customKey: string = 'defaultValue',
): number => {
	const candidate = component.value ? JSON.parse(component.value as string) : undefined
	if (typeof candidate === 'number' && !Number.isNaN(candidate)) return candidate
	const custom = getCustomObject(component)
	const raw = custom && customKey in custom ? (custom as Record<string, unknown>)[customKey] : undefined
	let parsed: number | undefined
	if (typeof raw === 'number') parsed = raw
	else if (typeof raw === 'string' && raw.trim() !== '') parsed = Number(raw)
	return typeof parsed === 'number' && !Number.isNaN(parsed) ? parsed : fallback
}

export const getInitialFormValue = (
	component: ProcessedFormComponent,
	fieldType: 'text' | 'number' | 'rating',
	customKey: string = 'defaultValue',
): string => {
	let candidate: unknown
	try {
		candidate = JSON.parse(component.value as string)
	} catch {
		candidate = component.value
	}

	if (candidate !== null && candidate !== undefined && candidate !== '') {
		return String(candidate)
	}

	const custom = getCustomObject(component)
	if (custom && customKey in custom && custom[customKey] !== null && custom[customKey] !== undefined) {
		const defaultVal = custom[customKey]
		if (defaultVal !== '') {
			return String(defaultVal)
		}
	}

	switch (fieldType) {
		case 'number':
			return ''
		case 'rating':
			return ''
		default:
			return ''
	}
}
